import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { FiPublicationActionType, ObjectTypes, PublicationType } from '@fi-shared/types';
import { IPublicationSetting, PublicationSettingModel } from '@fi-shared/models';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { isDefined } from '@fi-shared/utils';

const FiPublicationTypes = [
    { Id: PublicationType.Continous, TextId: 'common.continuous' },
    { Id: PublicationType.TimeLimited, TextId: 'common.time_limited' },
];

interface IShareType {
    shared?: boolean;
    title: string;
    publicationType: number | false;
}

@Component({
    selector: 'fi-edit-publication',
    templateUrl: './edit-publication.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./edit-publication.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiEditPublicationComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiEditPublicationComponent,
            multi: true,
        },
    ],
    host: { class: 'fi-edit-publication' },
    standalone: false,
})
export class FiEditPublicationComponent
    extends ControlValueAccessorValidatorBase<IPublicationSetting>
    implements OnInit, OnChanges
{
    @Input() minStartDate: string;
    @Input() ngDisabled: boolean;
    @Input() objectType: ObjectTypes;
    @Input() appearance: MatFormFieldAppearance;
    @Input() publicationMinRange: { startDateStr: string; endDateStr: string };
    @Input() inputErrorTextLabel: string;
    @Input() isStandardFormField: boolean = false;

    PublicationActionTypes: {
        Id: number;
        TextId: string;
    }[];
    selectPeriodSettings: {
        StartTimeProperty: string;
        EndTimeProperty: string;
    };
    selectedPublicationType: IShareType;

    shareTypes: IShareType[];

    private defaultStartDate: Date;
    private defaultEndDate: Date;

    constructor(private injector: Injector) {
        super(injector);

        this.shareTypes = [
            {
                title: 'common.not_shared',
                publicationType: false,
            },
        ];

        FiPublicationTypes.forEach((publicationType) => {
            this.shareTypes.push({
                shared: true,
                title: publicationType.TextId,
                publicationType: publicationType.Id,
            });
        });

        this.selectPeriodSettings = {
            StartTimeProperty: 'PublicationStartDate',
            EndTimeProperty: 'PublicationEndDate',
        };

        this.defaultStartDate = new Date();
        this.defaultEndDate = new Date();
        this.defaultStartDate.setHours(0, 0, 0, 0);
        this.defaultEndDate.setHours(23, 59, 59, 0);
        this.defaultEndDate.setMonth(this.defaultEndDate.getMonth() + 3);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.publicationMinRange) this.doPropagateChanges();
    }

    onDataChanged() {
        this.ngModel.IsPublicationSettingChanged = true;
        this.doPropagateChanges();
    }

    IsPublicationDatesValid(): boolean {
        if (this.ngModel.IsShared) {
            if (this.ngModel.PublicationType == PublicationType.Continous) {
                return this.ngModel.PublicationStartDate != null;
            } else if (this.IsTimeLimitedPublicationTypeSelected()) {
                return (
                    this.ngModel.PublicationStartDate != null &&
                    this.ngModel.PublicationEndDate != null &&
                    new Date(this.ngModel.PublicationStartDate) <= new Date(this.ngModel.PublicationEndDate)
                );
            }
        }

        return true;
    }

    ngOnInit() {
        this.selectedPublicationType = undefined;

        if (!this.ngModel) {
            // init new instance of publication setting
            this.ngModel = PublicationSettingModel.getNewInstance();
            this.onDataChanged();
        }
        if (this.ngModel.IsShared) {
            this.selectedPublicationType =
                this.shareTypes.find((shareType) => shareType.publicationType === this.ngModel.PublicationType) ??
                this.shareTypes[0];
        } else {
            this.selectedPublicationType = this.shareTypes[0];
        }

        if (!this.ngModel.PublicationStartDate) {
            this.ngModel.PublicationStartDate = this.defaultStartDate.toISOString();
            this.onDataChanged();
        }
        if (!this.ngModel.PublicationEndDate && this.ngModel.PublicationType === PublicationType.TimeLimited) {
            this.ngModel.PublicationEndDate = this.defaultEndDate.toISOString();
            this.onDataChanged();
        }

        this.PublicationActionTypes = this.getPublicationActionTypes((<any>this.ngModel).ObjectType || this.objectType);
        this.setShareTypesLabelsByObjectType();
    }

    private setShareTypesLabelsByObjectType() {
        if (this.objectType === ObjectTypes.ChecklistSchedule) {
            this.shareTypes.map((shareType) => {
                if (shareType.publicationType === PublicationType.Continous) {
                    shareType.title = 'common.continuously_operational';
                }
                if (shareType.publicationType === PublicationType.TimeLimited) {
                    shareType.title = 'common.time_limited_operational';
                }
                if (shareType.publicationType === false) {
                    shareType.title = 'common.not_operational';
                }
                return shareType;
            });
        }
    }

    IsTimeLimitedPublicationTypeSelected() {
        return this.ngModel.PublicationType == PublicationType.TimeLimited;
    }

    onSelectedPublicationTypeChange() {
        const newValue = this.selectedPublicationType.publicationType;
        if (newValue) {
            this.ngModel.IsShared = true;
            this.ngModel.PublicationType = newValue;
            if (!this.ngModel.PublicationActionType) {
                this.ngModel.PublicationActionType = FiPublicationActionType.UnpublishDocument;
            }
        } else {
            this.ngModel.IsShared = false;
        }
        this.onDataChanged();
    }

    private getPublicationActionTypes(objectType): { Id: number; TextId: string }[] {
        if (objectType === ObjectTypes.Document) {
            return [
                { Id: FiPublicationActionType.DeleteDocument, TextId: 'document.delete_page' },
                { Id: FiPublicationActionType.UnpublishDocument, TextId: 'document.unpublish_page' },
            ];
        }
        if (objectType === ObjectTypes.MediaBank) {
            return [
                { Id: FiPublicationActionType.DeleteDocument, TextId: 'mediabank.delete_file' },
                { Id: FiPublicationActionType.UnpublishDocument, TextId: 'mediabank.unpublish_file' },
            ];
        }
        if (
            objectType === ObjectTypes.CheckList ||
            objectType === ObjectTypes.Form ||
            objectType === ObjectTypes.ChecklistSchedule
        ) {
            return [{ Id: FiPublicationActionType.UnpublishDocument, TextId: 'unpublish' }];
        }
        throw new Error('wrong ObjectType of publication item');
    }

    isPublicationMinRageInvalid(): boolean {
        if (!!this.ngModel.PublicationType && isDefined(this.publicationMinRange)) {
            const maxStartTime: number = new Date(this.publicationMinRange?.startDateStr).getTime();
            const publicationStartTime: number = new Date(this.ngModel.PublicationStartDate).getTime();
            const minEndTime: number = new Date(this.publicationMinRange?.endDateStr).getTime();
            const publicationEndTime: number = new Date(this.ngModel.PublicationEndDate).getTime();

            return (
                publicationStartTime > maxStartTime ||
                (this.ngModel.PublicationType === PublicationType.TimeLimited && publicationEndTime < minEndTime)
            );
        }
    }

    doValidate(c: AbstractControl): ValidationErrors | any {
        const validationResult = {
            publicationdates: !this.IsPublicationDatesValid(),
            publicationMinRangeFit: this.isPublicationMinRageInvalid(),
        };
        return validationResult;
    }

    protected getContainerClasses(): { [key: string]: boolean } {
        return {
            'fix-margin':
                !this.ngModel.IsShared || (this.ngModel.IsShared && this.IsTimeLimitedPublicationTypeSelected()),
            'not-shared': !this.ngModel.IsShared,
        };
    }
}
